import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Store, Search, Package, MapPin, Phone, Mail, Star,
  Eye, Edit, Trash2, TrendingUp, ShoppingBag, Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminBoutiques() {
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [stats, setStats] = useState({
    totalPartners: 0,
    totalProducts: 0,
    activePartners: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [partnersRes, productsRes] = await Promise.all([
        axios.get(`${API}/partners`),
        axios.get(`${API}/products?limit=500`)
      ]);

      setPartners(partnersRes.data);
      setProducts(productsRes.data);

      setStats({
        totalPartners: partnersRes.data.length,
        totalProducts: productsRes.data.length,
        activePartners: partnersRes.data.filter(p => p.is_approved !== false).length
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  const getPartnerProducts = (partnerId) => {
    return products.filter(p => p.vendor_id === partnerId);
  };

  const deletePartner = async (partnerId) => {
    if (!window.confirm("Supprimer ce partenaire ? Tous ses produits seront également supprimés.")) return;
    try {
      await axios.delete(`${API}/admin/users/${partnerId}`);
      toast.success("Partenaire supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Produit supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredPartners = partners.filter(partner =>
    partner.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Total Partenaires</p>
                <p className="text-3xl font-bold">{stats.totalPartners}</p>
              </div>
              <Store className="w-10 h-10 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Total Produits</p>
                <p className="text-3xl font-bold">{stats.totalProducts}</p>
              </div>
              <ShoppingBag className="w-10 h-10 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Partenaires Actifs</p>
                <p className="text-3xl font-bold">{stats.activePartners}</p>
              </div>
              <Users className="w-10 h-10 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partners List */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Boutiques Partenaires
            </CardTitle>
            <div className="flex gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une boutique..."
                  className="pl-9 w-64 rounded-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button onClick={fetchData} variant="outline" className="rounded-full">
                🔄 Actualiser
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Chargement...</div>
          ) : filteredPartners.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Store className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>Aucune boutique partenaire trouvée</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPartners.map((partner) => {
                const partnerProducts = getPartnerProducts(partner.id);
                return (
                  <div
                    key={partner.id}
                    className="border rounded-xl overflow-hidden bg-white hover:shadow-lg transition-all"
                  >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-lg">{partner.business_name || partner.full_name}</h3>
                          <p className="text-sm opacity-80">{partner.shop_type || "Boutique générale"}</p>
                        </div>
                        <div className="bg-white/20 rounded-full px-2 py-1 text-xs">
                          {partnerProducts.length} produits
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-4">
                      <div className="space-y-2 text-sm mb-4">
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          {partner.email}
                        </p>
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          {partner.phone || "Non renseigné"}
                        </p>
                        {partner.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            {partner.address}
                          </p>
                        )}
                      </div>

                      {/* Products Preview */}
                      {partnerProducts.length > 0 && (
                        <div className="border-t pt-3 mb-3">
                          <p className="text-xs font-semibold text-muted-foreground mb-2">Produits récents</p>
                          <div className="flex gap-2 overflow-x-auto pb-2">
                            {partnerProducts.slice(0, 4).map((product) => (
                              <div key={product.id} className="flex-shrink-0 w-16">
                                {product.image_url ? (
                                  <img
                                    src={product.image_url}
                                    alt={product.name}
                                    className="w-16 h-16 object-cover rounded-lg"
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <Package className="w-6 h-6 text-gray-300" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 rounded-full"
                          onClick={() => setSelectedPartner(selectedPartner === partner.id ? null : partner.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {selectedPartner === partner.id ? "Masquer" : "Voir produits"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 rounded-full"
                          onClick={() => deletePartner(partner.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Products */}
                    {selectedPartner === partner.id && partnerProducts.length > 0 && (
                      <div className="border-t bg-slate-50 p-4">
                        <h4 className="font-semibold mb-3">Tous les produits ({partnerProducts.length})</h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {partnerProducts.map((product) => (
                            <div key={product.id} className="flex items-center gap-3 bg-white p-2 rounded-lg">
                              {product.image_url ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                                  <Package className="w-5 h-5 text-gray-300" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{product.name}</p>
                                <p className="text-sm text-muted-foreground">{product.price?.toLocaleString()} FCFA</p>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600"
                                onClick={() => deleteProduct(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
